<script lang="ts">
	import { CONTINENT_LABELS } from '$lib/api/velatlas/response.types';
	import CardBadge from '$lib/components/ui/CardBadge.svelte';
	import ImageHoverGallery from '$lib/components/ui/ImageHoverGallery.svelte';
	import type { ThumbnailCountry } from '$lib/features/country/countryThumbnail.type';
	import type { Language } from '$lib/features/country/language/language.type';
	import { LanguageService } from '$lib/features/country/language/LanguageService';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import { fly } from 'svelte/transition';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import CityIcon from '~icons/mdi/city';
	import IconLanguage from '~icons/mdi/language';

	const { country, eagerLoadCoverImage }: { country: ThumbnailCountry; eagerLoadCoverImage: boolean } = $props();

	let isCardHovered = $state<boolean>(false);

	const language = $derived<Language>(LanguageService.getByCode(country.langCode));
</script>

{const countryNav = $derived<NavLink>(UrlUtil.toCountry({ countrySlug: country.slug }))}
<a
	class="group relative block aspect-4/3 w-full overflow-hidden rounded-box shadow-lg transition-all duration-500"
	title="View {country.englishName}"
	href="/{countryNav.href}"
	data-sveltekit-preload-data={countryNav.preloadData}
	data-sveltekit-preload-code={countryNav.preloadCode}
>
	<ImageHoverGallery
		images={country.images}
		eagerLoading={eagerLoadCoverImage}
		onHoverChange={(isHovered) => (isCardHovered = isHovered)}
	/>

	{#if !isCardHovered}
		{@render Gradient()}
	{/if}

	<div class="pointer-events-none absolute inset-0 flex flex-col justify-end p-8 text-white">
		{#if !isCardHovered}
			<div class="absolute top-6 right-6" in:fly={{ y: 20, duration: 300 }} out:fly={{ y: 20, duration: 300 }}>
				<CardBadge text={CONTINENT_LABELS[country.continent].toUpperCase()} />
			</div>

			<div
				class="transform text-white transition-transform duration-500 group-hover:-translate-y-2"
				in:fly={{ y: 20, duration: 300 }}
				out:fly={{ y: 20, duration: 300 }}
			>
				<h3 class="mb-4 text-4xl leading-none">
					{country.englishName}
				</h3>

				<div class="flex items-center gap-6 text-sm font-medium">
					{@render Stats()}
				</div>
			</div>
		{/if}

		<div
			class="absolute right-8 bottom-8 translate-y-8 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100"
		>
			{@render CTA()}
		</div>
	</div>
</a>

{#snippet Stats()}
	<div class="flex items-center gap-2">
		<IconLanguage class="text-lg" />
		<span>{language.name}</span>
	</div>

	<div class="flex items-center gap-2">
		<CityIcon class="text-lg" />
		<span>{country.citiesCounter} {country.citiesCounter === 1 ? 'city' : 'cities'}</span>
	</div>
{/snippet}

{#snippet CTA()}
	<div class="btn btn-circle shadow-lg btn-primary">
		<IconArrowRight class="h-6 w-6" />
	</div>
{/snippet}

{#snippet Gradient()}
	<div
		class="pointer-events-none absolute inset-0 bg-linear-to-t from-black/90 via-black/60 to-transparent opacity-80 transition-opacity group-hover:opacity-90"
	></div>
{/snippet}
